import React from "react";
import CommonButton from "../../CommonButton";
import {
  Calendar,
  PawPrint,
  Star,
  Eye,
  EyeOff,
  SlidersHorizontal
} from "lucide-react";
import { IGetPetResponse } from "../../../types/Response/AdminResponse";
import { useUpdatePetStatusMutation } from "../../../redux/actions/Admin/PetsAdmin";

interface PetCardComponentProps {
  data: IGetPetResponse[];
  handleBooknow: (data: IGetPetResponse) => void;
  showButton?: boolean;
}

export const PetCardComponent: React.FC<PetCardComponentProps> = ({ data, handleBooknow, showButton = true }) => {
  const [updatePetStatus, { isLoading: isStatusUpdating }] = useUpdatePetStatusMutation();

  const statusOptionsList = [
    { label: "Available", value: 1 },
    { label: "Reserved", value: 2 },
    { label: "Sold", value: 3 },
    { label: "Adopted", value: 4 }
  ];

  // Helper mapping current statuses cleanly into string forms
  const getStatusNumber = (status: string | number): number => {
    if (typeof status === 'number') return status;
    const match = statusOptionsList.find(s => s.label === status);
    return match ? match.value : 1;
  };

  const handleQuickStatusToggle = async (
    e: React.MouseEvent,
    pet: IGetPetResponse,
    updateFields: { status?: number; featured?: boolean; active?: boolean }
  ) => {
    e.stopPropagation();

    const currentStatusNum = getStatusNumber(pet.petStatus);
    const payload = {
      petId: pet.id,
      petStatus: updateFields.status !== undefined ? updateFields.status : currentStatusNum,
      isFeatured: updateFields.featured !== undefined ? updateFields.featured : !!pet.isFeatured,
      isActive: updateFields.active !== undefined ? updateFields.active : !((pet as any).isActive === false)
    };

    try {
      await updatePetStatus(payload).unwrap();
    } catch (err) {
      console.error("Fast-patch card level modification failure:", err);
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 px-2">
      {data.map((pet, i) => {
        const rawStatusNum = getStatusNumber(pet.petStatus);
        const isPetFeatured = !!pet.isFeatured;

        // Dynamic fallback mapping property check safely handles variant response properties keys
        const isPetActive = (pet as any).isActive !== undefined ? !!(pet as any).isActive : true;

        return (
          <div
            key={pet.id || i}
            onClick={() => handleBooknow(pet)}
            className={`bg-white rounded-2xl overflow-hidden group hover:shadow-xl transition-all duration-300 border border-mediumgray flex flex-col shadow-xs cursor-pointer relative ${
              !isPetActive ? 'bg-lightgray border-mediumgray grayscale opacity-65' : ''
            }`}
          >
            {/* Image Container Section */}
            <div className="relative h-48 sm:h-52 overflow-hidden bg-slate-50">
              <img
                src={pet.productImage || (pet.subImages && pet.subImages[0]) || 'https://images.unsplash.com/photo-1543466835-00a7907e9de1?w=500'}
                alt={pet.petName}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />

              {pet.tag && (
                <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-xs px-2.5 py-1 rounded-lg text-[10px] font-black text-logoHeading uppercase tracking-wider shadow-xs border border-gray-100">
                  {pet.tag}
                </div>
              )}

              {/* FLOATING QUICK ACCESSIBLE SYSTEM STATUS BADGES */}
              <div className="absolute top-3 right-3 flex items-center gap-1.5">
                <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider shadow-xs border bg-white/95 backdrop-blur-xs ${rawStatusNum === 1 ? 'text-green-700 border-green-200' :
                  rawStatusNum === 2 ? 'text-amber-700 border-amber-200' :
                    'text-slate-500 border-gray-200'
                  }`}>
                  {pet.petStatus || "Available"}
                </span>
              </div>
            </div>

            {/* Core Body Section */}
            <div className="p-4 flex-1 flex flex-col justify-between space-y-3 bg-white">
              <div>
                <div className="flex justify-between items-start gap-2">
                  <h3 className="text-base md:text-lg font-black text-logoHeading tracking-tight line-clamp-1">
                    {pet.petName}
                  </h3>
                  <span className="text-[10px] uppercase tracking-wider bg-lightgray text-logoHeading px-2 py-0.5 rounded-md font-extrabold shrink-0 border border-mediumgray">
                    {pet.gender || "Male"}
                  </span>
                </div>

                {/* Characteristics Tag pills row */}
                <div className="flex items-center gap-1.5 flex-wrap mt-2">
                  <span className="inline-flex items-center gap-1 bg-green-50/60 text-green-800 text-[11px] font-bold px-2.5 py-1 rounded-lg border border-green-100">
                    <PawPrint size={12} className="text-green-600" />
                    {pet.breed}
                  </span>
                  <span className="inline-flex items-center gap-1 bg-blue-50/60 text-blue-800 text-[11px] font-bold px-2.5 py-1 rounded-lg border border-blue-100">
                    <Calendar size={12} className="text-blue-600" />
                    {typeof pet.age === 'number' ? `${pet.age} Months` : String(pet.age).includes('-') ? 'Baby' : pet.age}
                  </span>
                </div>
              </div>

              {/* =========================================================
                  ADMIN CONSOLE OVERLAY ROW
                  ========================================================= */}
              <div className="pt-3 border-t border-gray-100 mt-2 space-y-2.5">
                <div className="flex items-center justify-between gap-2">

                  {/* Quick Dropdown Selector */}
                  <div className="flex-1" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={rawStatusNum}
                      disabled={isStatusUpdating}
                      onChange={(e) => handleQuickStatusToggle(e as any, pet, { status: Number(e.target.value) })}
                      className="w-full bg-lightgray border border-mediumgray rounded-xl px-2.5 py-1.5 text-[11px] font-black text-gray-700 outline-none cursor-pointer focus:border-mainBrandColor focus:bg-white transition"
                    >
                      {statusOptionsList.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>

                  {/* Operational Feature and Toggle Tweak controls */}
                  <div className="flex items-center gap-1 shrink-0">

                    {/* Featured Home Banner Toggle */}
                    <button
                      type="button"
                      disabled={isStatusUpdating}
                      onClick={(e) => handleQuickStatusToggle(e, pet, { featured: !isPetFeatured })}
                      className={`p-1.5 rounded-xl border transition cursor-pointer ${isPetFeatured
                        ? 'bg-amber-50 border-amber-300 text-amber-500'
                        : 'bg-white border-mediumgray text-gray-300 hover:text-gray-400'
                        }`}
                      title="Featured Status Toggle"
                    >
                      <Star className={`w-3.5 h-3.5 ${isPetFeatured ? 'fill-amber-400' : ''}`} />
                    </button>

                    {/* Active System State Toggle */}
                    <button
                      type="button"
                      disabled={isStatusUpdating}
                      onClick={(e) => handleQuickStatusToggle(e, pet, { active: !isPetActive })}
                      className={`p-1.5 rounded-xl border transition cursor-pointer ${isPetActive
                        ? 'bg-green-50 border-green-200 text-mainBrandColor'
                        : 'bg-rose-50 border-rose-200 text-rose-500'
                        }`}
                      title={isPetActive ? "Active Visibility" : "Deactivated Hidden"}
                    >
                      {isPetActive ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    </button>

                  </div>
                </div>

                {/* Primary navigation trigger callback rendering block */}
                {showButton && (
                  <CommonButton
                    text="Adopt Now"
                    variant="filled"
                    rounded="md"
                    className="w-full shadow-2xs"
                    textContainerClassName="py-2 text-xs uppercase tracking-wider font-extrabold"
                    onClick={() => {
                      handleBooknow(pet);
                    }}
                  />
                )}
              </div>

            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PetCardComponent;